// Cross-link marriage/$nationality pages → embassy/$country pages.
// Forward edges to (a) embassy hub, (b) embassy/$country/marriage-certificate,
// (c) embassy/$country/power-of-attorney, (d) embassy/$country/birth-certificate.
// Boosts internal-linking depth for embassy cluster + helps GoogleBot crawl
// the marriage→embassy logical chain.

import { Link } from "@tanstack/react-router";
import { Building2, FileCheck2, Stamp, ArrowRight } from "lucide-react";
import { getEmbassy } from "@/data/embassies";
import { getEmbassyService } from "@/data/embassy-services";

// Nationality slug → embassy country slug. Keep in sync with
// src/data/marriage-nationalities.ts and src/data/embassies.ts.
const NATIONALITY_TO_EMBASSY: Record<string, string> = {
  american: "usa",
  canadian: "canada",
  british: "uk",
  german: "germany",
  french: "france",
  italian: "italy",
  spanish: "spain",
  dutch: "netherlands",
  belgian: "belgium",
  swiss: "switzerland",
  swedish: "sweden",
  norwegian: "norway",
  danish: "denmark",
  finnish: "finland",
  austrian: "austria",
  portuguese: "portugal",
  irish: "ireland",
  japanese: "japan",
  korean: "korea",
  chinese: "china",
  indian: "india",
  indonesian: "indonesia",
  malaysian: "malaysia",
  filipino: "philippines",
  singaporean: "singapore",
  vietnamese: "vietnam",
  australian: "australia",
  "new-zealander": "new-zealand",
  emirati: "uae",
  // No embassy partner (skip component gracefully):
  //   argentinian, brazilian, mexican, czech, polish, russian,
  //   ukrainian, turkish, israeli, egyptian, iranian, pakistani,
  //   south-african
};

type Props = {
  nationality: string;     // marriage nationality slug
  nationalityTh: string;   // e.g. "ชาวอเมริกัน"
  countryTh: string;       // e.g. "สหรัฐอเมริกา"
};

export function MarriageEmbassyLinks({ nationality, nationalityTh, countryTh }: Props) {
  const embassySlug = NATIONALITY_TO_EMBASSY[nationality];
  if (!embassySlug) return null;
  const e = getEmbassy(embassySlug);
  if (!e) return null;

  // Forward edges to embassy hub + 3 most-relevant services
  const services = (["marriage-certificate", "birth-certificate", "power-of-attorney"] as const)
    .map((s) => ({ slug: s, svc: getEmbassyService(s) }))
    .filter((x) => x.svc) as { slug: string; svc: NonNullable<ReturnType<typeof getEmbassyService>> }[];

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
        เอกสารสถานทูต {e.flag} {e.nameTh}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        ดำเนินเอกสารผ่านสถานทูต{countryTh}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        นอกจากจดทะเบียนสมรสกับ{nationalityTh} เราจัดทำเอกสารผ่านสถานทูต{countryTh}ครบทุกประเภท
        — Affidavit, Single Status, Power of Attorney, แปล + รับรอง MFA / Apostille.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/embassy/$country"
          params={{ country: e.slug }}
          className="group flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/40 p-4 transition hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md"
          data-track-zone="marriage-embassy-hub"
        >
          <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">
              สถานทูต{e.nameTh} — ภาพรวม
            </div>
            <div className="mt-0.5 text-[11px] text-slate-600">
              ค่าบริการเริ่ม ฿{e.serviceFeeTh.toLocaleString()} · {e.turnaround}
            </div>
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 group-hover:gap-1.5">
              ดูทุกบริการ <ArrowRight className="h-3 w-3" />
            </div>
          </div>
        </Link>

        {services.map(({ slug, svc }) => (
          <Link
            key={slug}
            to="/embassy/$country/$service"
            params={{ country: e.slug, service: slug }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
            data-track-zone="marriage-embassy-service"
          >
            {slug === "marriage-certificate" ? (
              <FileCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            ) : (
              <Stamp className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            )}
            <div className="flex-1">
              <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">
                {svc.nameTh}
              </div>
              <div className="mt-0.5 line-clamp-2 text-[11px] text-slate-600">
                ที่สถานทูต{e.nameTh} — {svc.summary}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 group-hover:gap-1.5">
                อ่านขั้นตอน <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
